import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Tier } from '@backrs_ui/common-ui';

import {
  CareerPathWithScore,
  CareerUserResponseAnswer,
  CareerUserResponseSet,
  SchoolLevel,
} from '@backrs_ui/grow-ui/src/models/models';
import { ScoreCalculationResult } from '@backrs_ui/grow-ui/src/utils/calculateSurveyResults';

import { v4 as uuidv4 } from 'uuid';

import { updateResponseSetOnAPI } from './thunks/updateResponseSet.thunk';
import {
  getMostRecentUserResponseSetFromAPI,
  getUserResponseSetFromAPI,
  postAPISurveyResults,
} from './thunks/userResponse.thunk';
import { BlueprintAspirationsCategories } from '../blueprint/blueprint.lubav.api';
import { logout } from '../session/logoutThunk';
import { RootState } from '../store';

type UserResponseState = Readonly<{
  /** The user's survey responses as they're filling out the survey */
  userResponseSet: CareerUserResponseSet;

  /** Whether the draft has already been submitted as a blueprint */
  draftSubmitted: boolean;

  /** The results from the users survey, or the most recent survey results from the API (both are the same) */
  mostRecentUserResponseSet?: CareerUserResponseSet | null;

  /** Career Path score results */
  results?: {
    tier: Tier;
    careerPathSuggestions: CareerPathWithScore[];
    careerResults: ScoreCalculationResult;
  };
  /** Aspirations categories LLM content */
  aspirationsCategories?: BlueprintAspirationsCategories | null;

  /** Loading/error states */
  loadingMostRecentUserResponseSet: boolean;
  loading: boolean;
  error: string | null;
}>;

const initialState: UserResponseState = {
  userResponseSet: {
    id: uuidv4(),
    user_responses: [],
  },
  draftSubmitted: false,
  mostRecentUserResponseSet: null,
  aspirationsCategories: null,
  loadingMostRecentUserResponseSet: true,
  loading: false,
  error: null,
};

export const userResponseSetSlice = createSlice({
  name: 'userResponseSet',
  initialState,
  reducers: {
    resetUserResponseSet: (state) => {
      return {
        ...state,
        userResponseSet: initialState.userResponseSet,
        draftSubmitted: false,
      };
    },
    clearDraftSubmitted: (state) => {
      return {
        ...state,
        draftSubmitted: false,
      };
    },
    addUsersSchoolLevel: (state, action: PayloadAction<SchoolLevel>) => {
      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          school_level: action.payload,
        },
      };
    },
    addUsersZipCode: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          zip_code: action.payload,
        },
      };
    },
    addUsersGraduationYear: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          graduation_year: action.payload,
        },
      };
    },
    addUsersSchool: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          school: action.payload,
        },
      };
    },
    addUsersStartTerm: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          start_term: action.payload,
        },
      };
    },
    addUsersStartYear: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          start_year: action.payload,
        },
      };
    },
    addUserQuestionResponse: (state, action: PayloadAction<CareerUserResponseAnswer>) => {
      const updatedResponse = action.payload;
      const existingResponseIndex = state.userResponseSet.user_responses.findIndex(
        (response) => response.id === updatedResponse.id,
      );

      if (existingResponseIndex !== -1) {
        return {
          ...state,
          userResponseSet: {
            ...state.userResponseSet,
            user_responses: state.userResponseSet.user_responses.map((response, index) =>
              index === existingResponseIndex ? updatedResponse : response,
            ),
          },
        };
      }

      return {
        ...state,
        userResponseSet: {
          ...state.userResponseSet,
          user_responses: [...state.userResponseSet.user_responses, updatedResponse],
        },
      };
    },
    addUserSurveyResults: (
      state,
      action: PayloadAction<{
        tier: Tier;
        careerPathSuggestions: CareerPathWithScore[];
        careerResults: ScoreCalculationResult;
      }>,
    ) => {
      return {
        ...state,
        results: action.payload,
      };
    },
    updateResponseSetWithAPIResponse: (state, action: PayloadAction<CareerUserResponseSet>) => {
      return {
        ...state,
        userResponseSet: action.payload,
      };
    },
    setMostRecentResponseLoading: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        loadingMostRecentUserResponseSet: action.payload,
      };
    },
    setAspirationsCategories: (state, action: PayloadAction<BlueprintAspirationsCategories>) => {
      return {
        ...state,
        aspirationsCategories: action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // POST API SURVEY RESULTS
      .addCase(postAPISurveyResults.fulfilled, (state, action) => {
        return {
          ...state,
          draftSubmitted: true,
          mostRecentUserResponseSet: action.payload.userResponseSet,
          results: action.payload.results,
          loadingMostRecentUserResponseSet: false,
          loading: false,
        };
      })
      .addCase(postAPISurveyResults.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          error: (action.payload as string) ?? 'Failed to submit survey results',
          loadingMostRecentUserResponseSet: false,
        };
      })
      .addCase(postAPISurveyResults.pending, (state) => {
        return {
          ...state,
          loading: true,
          error: null,
          loadingMostRecentUserResponseSet: false,
        };
      })

      // GET SURVEY RESULTS FROM API BY ID
      .addCase(getUserResponseSetFromAPI.pending, (state) => {
        return {
          ...state,
          loading: true,
          error: null,
        };
      })

      .addCase(getUserResponseSetFromAPI.fulfilled, (state, action) => {
        return {
          ...state,
          userResponseSet: action.payload.userResponseSet,
          results: action.payload.results,
          loading: false,
        };
      })

      .addCase(getUserResponseSetFromAPI.rejected, (state, action) => {
        return {
          ...state,
          loading: false,
          error: action.payload as string,
        };
      })

      // GET MOST SURVEY RESULTS FROM API
      .addCase(getMostRecentUserResponseSetFromAPI.pending, (state) => {
        return {
          ...state,
          loadingMostRecentUserResponseSet: true,
          error: null,
        };
      })

      .addCase(getMostRecentUserResponseSetFromAPI.fulfilled, (state, action) => {
        if (!action.payload) {
          return {
            ...state,
            mostRecentUserResponseSet: null,
            loadingMostRecentUserResponseSet: false,
          };
        }

        return {
          ...state,
          mostRecentUserResponseSet: action.payload.userResponseSet,
          results: action.payload.results,
          aspirationsCategories: (() => {
            const cats = action.payload.aspirationsCategories;

            if (cats?.lifestyle_and_home) return cats;
            if (cats)
              console.warn(
                'Aspirations categories missing lifestyle_and_home — discarding all categories',
              );

            return null;
          })(),
          loadingMostRecentUserResponseSet: false,
        };
      })

      .addCase(getMostRecentUserResponseSetFromAPI.rejected, (state, action) => {
        return {
          ...state,
          loadingMostRecentUserResponseSet: false,
          error: action.payload as string,
        };
      })

      // UPDATE RESPONSE SET (Explore Tradeoffs Lock In)
      .addCase(updateResponseSetOnAPI.fulfilled, (state, action) => {
        return {
          ...state,
          mostRecentUserResponseSet: action.payload.userResponseSet,
          aspirationsCategories: null, // Stale — will regenerate on next view
        };
      })
      .addCase(updateResponseSetOnAPI.rejected, (state, action) => {
        return {
          ...state,
          error: (action.payload as string) ?? 'Failed to update response set',
        };
      })

      // LOGOUT
      .addCase(logout, (state) => {
        state = initialState;

        return state;
      });
  },
});

// Other code such as selectors can use the imported `RootState` type
export const selectUserResponseSet = (state: RootState) => state.userResponseSet.userResponseSet;
export const selectMostRecentUserResponseSet = (state: RootState) =>
  state.userResponseSet.mostRecentUserResponseSet;
export const selectUserSurveyResults = (state: RootState) => state.userResponseSet.results;
export const selectAspirationsCategories = (state: RootState) =>
  state.userResponseSet.aspirationsCategories;

export const userResponseSetReducer = userResponseSetSlice.reducer;

export const {
  addUserQuestionResponse,
  addUsersSchoolLevel,
  addUsersZipCode,
  addUsersGraduationYear,
  addUsersSchool,
  addUsersStartTerm,
  addUsersStartYear,
  addUserSurveyResults,
  updateResponseSetWithAPIResponse,
  resetUserResponseSet,
  clearDraftSubmitted,
  setMostRecentResponseLoading,
  setAspirationsCategories,
} = userResponseSetSlice.actions;
